import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { vehicleContext } from '../../../VehicleProvider';
import type { VehicleDetails } from '../../../../types';

interface TechnicalDataProps {
  title: string | null;
  categories: {
    spec: string | null;
    body: string | undefined | null;
  }[];
}

type UseTechnicalData = () => TechnicalDataProps[];

const useTechnicalData: UseTechnicalData = () => {
  const { vehicle } = useContext(vehicleContext);
  const { t } = useTranslation();

  const { technicalSpecifications } = vehicle as VehicleDetails;

  if (!vehicle) return [];

  const {
    engineType,
    displacement,
    maxOutput,
    maxTorque,
    gearbox,
    frontAxle,
    rearAxle,
    brakes,
    steering,
    unladenWeight,
    grossWeightLimit,
    luggageCompartmentCapacity,
    tankCapacity,
    topSpeed,
    acceleration,
    fuelType,
    fuelData,
  } = technicalSpecifications;

  const { fuel_electrical: fuelElectrical, fuel_petrol: fuelPetrol } = fuelData;

  const technicalData = [
    {
      title: t('featuresAndSpecsSection.categories.technicalData.sections.engine.title'),
      categories: [
        {
          spec: t('featuresAndSpecsSection.categories.technicalData.sections.engine.engineType'),
          body: engineType,
        },
      ],
    },
    {
      title: t('featuresAndSpecsSection.categories.technicalData.sections.performance.title'),
      categories: [
        {
          spec: t(
            'featuresAndSpecsSection.categories.technicalData.sections.performance.displacement',
          ),
          body: displacement,
        },
        {
          spec: t(
            'featuresAndSpecsSection.categories.technicalData.sections.performance.maxOutput',
          ),
          body: maxOutput,
        },
        {
          spec: t(
            'featuresAndSpecsSection.categories.technicalData.sections.performance.maxTorque',
          ),
          body: maxTorque,
        },
      ],
    },
    {
      title: t('featuresAndSpecsSection.categories.technicalData.sections.driveline.title'),
      categories: [
        {
          spec: t(
            'featuresAndSpecsSection.categories.technicalData.sections.driveline.transmission',
          ),
          body: gearbox,
        },
      ],
    },
    {
      title: t('featuresAndSpecsSection.categories.technicalData.sections.suspension.title'),
      categories: [
        {
          spec: t('featuresAndSpecsSection.categories.technicalData.sections.suspension.front'),
          body: frontAxle,
        },
        {
          spec: t('featuresAndSpecsSection.categories.technicalData.sections.suspension.rear'),
          body: rearAxle,
        },
      ],
    },
    {
      title: t('featuresAndSpecsSection.categories.technicalData.sections.brakes.title'),
      categories: [
        {
          spec: t('featuresAndSpecsSection.categories.technicalData.sections.brakes.brakeSystem'),
          body: brakes,
        },
      ],
    },
    {
      title: t('featuresAndSpecsSection.categories.technicalData.sections.steering.title'),
      categories: [
        {
          spec: t('featuresAndSpecsSection.categories.technicalData.sections.steering.steering'),
          body: steering,
        },
      ],
    },
    {
      title: t('featuresAndSpecsSection.categories.technicalData.sections.weights.title'),
      categories: [
        {
          spec: t(
            'featuresAndSpecsSection.categories.technicalData.sections.weights.unladenWeight',
          ),
          body: unladenWeight,
        },
        {
          spec: t(
            'featuresAndSpecsSection.categories.technicalData.sections.weights.grossWeightLimit',
          ),
          body: grossWeightLimit,
        },
      ],
    },
    {
      title: t('featuresAndSpecsSection.categories.technicalData.sections.volumes.title'),
      categories: [
        {
          spec: t('featuresAndSpecsSection.categories.technicalData.sections.volumes.luggage'),
          body: luggageCompartmentCapacity,
        },
        {
          spec: t('featuresAndSpecsSection.categories.technicalData.sections.volumes.fuelTank'),
          body: tankCapacity,
        },
      ],
    },
    {
      title: t('featuresAndSpecsSection.categories.technicalData.sections.fuelPerformance.title'),
      categories: [
        {
          spec: t(
            'featuresAndSpecsSection.categories.technicalData.sections.fuelPerformance.topSpeed',
          ),
          body: topSpeed,
        },
        {
          spec: t(
            'featuresAndSpecsSection.categories.technicalData.sections.fuelPerformance.acceleration',
          ),
          body: acceleration,
        },
      ],
    },
    {
      title: t('featuresAndSpecsSection.categories.technicalData.sections.consumption.title'),
      categories: [
        {
          spec: t('featuresAndSpecsSection.categories.technicalData.sections.consumption.fuel'),
          body: fuelType,
        },
        {
          spec: t('featuresAndSpecsSection.categories.technicalData.sections.consumption.fuelCity'),
          body: `${fuelPetrol?.urban}${fuelElectrical?.urban ? `, ${fuelElectrical.urban}` : ''}`,
        },
        {
          spec: t(
            'featuresAndSpecsSection.categories.technicalData.sections.consumption.fuelHighway',
          ),
          body: `${fuelPetrol?.extraUrban}${
            fuelElectrical?.extraUrban ? `, ${fuelElectrical.extraUrban}` : ''
          }`,
        },
        {
          spec: t(
            'featuresAndSpecsSection.categories.technicalData.sections.consumption.fuelCombined',
          ),
          body: `${fuelPetrol?.combined}${
            fuelElectrical?.combined ? `, ${fuelElectrical.combined}` : ''
          }`,
        },
      ],
    },
  ];

  return technicalData;
};

export default useTechnicalData;
